declare var Tafgeet: any;

function firstDefined(...values: any[]): any {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
}

function escapeHtml(value: any): string {
  return String(firstDefined(value, "-"))
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(value: any): string {
  if (!value) {
    return "-";
  }

  const datePart = String(value).slice(0, 10);
  const parts = datePart.split("-");
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  return datePart;
}

function formatAmount(value: any): string {
  const numberValue = Number(value);
  if (isNaN(numberValue)) {
    return String(value || 0);
  }

  return numberValue.toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });
}

function amountInWords(value: any): string {
  try {
    return new Tafgeet(Number(value) || 0, "").parse();
  } catch (error) {
    return "-";
  }
}

function getLatestReceipt(payment: any = {}): any {
  const receipts =
    payment && Array.isArray(payment.receipts) ? payment.receipts : [];

  return receipts.reduce((latest, receipt) => {
    if (!latest) {
      return receipt;
    }

    const latestDate = Date.parse(latest.created_at || latest.updated_at || "");
    const receiptDate = Date.parse(
      receipt.created_at || receipt.updated_at || ""
    );

    if (!isNaN(receiptDate) && (isNaN(latestDate) || receiptDate > latestDate)) {
      return receipt;
    }

    const datesAreEqual =
      !isNaN(receiptDate) &&
      !isNaN(latestDate) &&
      receiptDate === latestDate;
    const bothDatesAreMissing = isNaN(receiptDate) && isNaN(latestDate);

    if (
      (datesAreEqual || bothDatesAreMissing) &&
      Number(receipt.id || 0) > Number(latest.id || 0)
    ) {
      return receipt;
    }

    return latest;
  }, null);
}

export function resolvePaymentMethod(
  payment: any = {},
  receipt: any = {},
  reservation: any = {}
): {
  methodType: "cash" | "transfer" | "cheque" | "visa" | "other";
  paymentMethod: string;
  receiptTitle: string;
} {
  const receiptsList =
    payment && Array.isArray(payment.receipts) ? payment.receipts : [];
  const activeReceipt =
    receipt && Object.keys(receipt).length > 0
      ? receipt
      : getLatestReceipt({ receipts: receiptsList }) || {};

  const candidates = [
    activeReceipt.type_string,
    activeReceipt.payment_method,
    activeReceipt.method,
    payment.partially_method_string,
    payment.partially_method_name,
    payment.collect_types,
    payment.payment_method,
    payment.type_string,
    payment.installment_type,
    reservation && reservation.payment_method,
    reservation && reservation.payment_type,
  ];

  const rawCandidate = candidates.find(
    (c) => c !== undefined && c !== null && String(c).trim() !== ""
  );
  const lowerCandidate = rawCandidate ? String(rawCandidate).toLowerCase() : "";

  const numericCode =
    activeReceipt.partially_method ||
    activeReceipt.type ||
    activeReceipt.payment_method_id ||
    payment.partially_method ||
    payment.slip_type ||
    payment.payment_method_id ||
    (reservation ? reservation.payment_method_id : null);

  const num = Number(numericCode);

  let isCash = false;
  let isTransfer = false;
  let isCheque = false;
  let isVisa = false;

  if (num === 1) {
    isCash = true;
  } else if (num === 2) {
    isVisa = true;
  } else if (num === 3) {
    isTransfer = true;
  } else if (num === 4) {
    isCheque = true;
  } else if (
    lowerCandidate.includes("cash") ||
    lowerCandidate.includes("نقدا") ||
    lowerCandidate.includes("نقدًا") ||
    lowerCandidate.includes("نقدية")
  ) {
    isCash = true;
  } else if (
    lowerCandidate.includes("transfer") ||
    lowerCandidate.includes("تحويل")
  ) {
    isTransfer = true;
  } else if (
    lowerCandidate.includes("cheque") ||
    lowerCandidate.includes("check") ||
    lowerCandidate.includes("شيك")
  ) {
    isCheque = true;
  } else if (
    lowerCandidate.includes("visa") ||
    lowerCandidate.includes("فيزا")
  ) {
    isVisa = true;
  } else if (
    payment.cheque_number &&
    String(payment.cheque_number).trim() !== "" &&
    String(payment.cheque_number).trim() !== "-"
  ) {
    isCheque = true;
  } else if (
    payment.transaction_number &&
    String(payment.transaction_number).trim() !== "" &&
    String(payment.transaction_number).trim() !== "-"
  ) {
    isTransfer = true;
  }

  if (isCash) {
    return {
      methodType: "cash",
      paymentMethod: "نقدًا",
      receiptTitle: "إيصال استلام نقدية",
    };
  }

  if (isTransfer) {
    return {
      methodType: "transfer",
      paymentMethod: "تحويل",
      receiptTitle: "إيصال استلام تحويل",
    };
  }

  if (isCheque) {
    return {
      methodType: "cheque",
      paymentMethod: "شيك",
      receiptTitle: "إيصال استلام شيك",
    };
  }

  if (isVisa) {
    return {
      methodType: "visa",
      paymentMethod: "فيزا",
      receiptTitle: "إيصال استلام فيزا",
    };
  }

  return {
    methodType: "other",
    paymentMethod: rawCandidate ? String(rawCandidate) : "دفعة",
    receiptTitle: "إيصال استلام نقدية / شيكات",
  };
}

function receiptCopy(copyLabel: string, payment: any, reservation: any): string {
  const receipt = getLatestReceipt(payment) || {};
  const sharingLead =
    reservation &&
    reservation.sharing_leads &&
    reservation.sharing_leads.length
      ? reservation.sharing_leads[0]
      : {};
  const amount = firstDefined(
    receipt && receipt.amount,
    payment.amount_received,
    payment.amount
  );
  const { methodType, paymentMethod, receiptTitle } = resolvePaymentMethod(
    payment,
    receipt,
    reservation
  );
  const paymentNumber = firstDefined(
    payment.cheque_number,
    receipt && receipt.cheque_number,
    payment.transaction_number,
    receipt && receipt.transaction_number,
    "-"
  );
  const receiptDate = formatDate(
    firstDefined(
      payment.collection_date,
      receipt && receipt.created_at,
      payment.date
    )
  );
  const customerName = firstDefined(
    sharingLead.lead_name,
    payment.lead_name,
    "-"
  );
  const nationalId = firstDefined(
    sharingLead.lead_national_id,
    sharingLead.national_id,
    "-"
  );
  const bankName = firstDefined(
    payment.store_status_text,
    payment.bank && payment.bank.name,
    "-"
  );
  const unitSerial = firstDefined(
    reservation && reservation.unit && reservation.unit.serial,
    payment.unit_serial,
    "-"
  );
  const projectName = firstDefined(
    reservation &&
      reservation.unit &&
      reservation.unit.project &&
      reservation.unit.project.name,
    payment.project_name,
    "-"
  );
  const chequeBookName = firstDefined(
    payment.cheque_book_name,
    reservation && reservation.cheque_book_name,
    ""
  );

  return `
    <section class="receipt-copy">
      <header class="receipt-header">
        <div class="serial">${escapeHtml(receipt && receipt.serial)}</div>
        <div class="title">
          <h1>${escapeHtml(receiptTitle)}</h1>
          <div>${escapeHtml(copyLabel)}</div>
        </div>
        <div class="logo">
          ${
            reservation && reservation.installment_logo
              ? `<img src="${escapeHtml(
                  reservation.installment_logo.url
                )}" alt="" />`
              : ""
          }
        </div>
      </header>

      <div class="row date-row">
        <span class="field"><b>التاريخ:</b> ${escapeHtml(receiptDate)}</span>
      </div>

      <div class="row two-columns">
        <span class="field"><b>ورد من السيد:</b> ${escapeHtml(
          customerName
        )}</span>
        <span class="field"><b>الرقم القومي / جواز السفر:</b> ${escapeHtml(
          nationalId
        )}</span>
      </div>

      <div class="row amount-row">
        <span class="field"><b>مبلغ وقدره:</b> ${escapeHtml(
          formatAmount(amount)
        )} جنيه مصري</span>
        <span class="words">(${escapeHtml(amountInWords(amount))})</span>
      </div>

      <div class="row payment-row">
        <span class="field"><b>وسيلة الدفع:</b> ${escapeHtml(
          paymentMethod
        )}</span>
        <span class="field"><b>رقم:</b> ${escapeHtml(paymentNumber)}</span>
        <span class="field"><b>بتاريخ:</b> ${escapeHtml(receiptDate)}</span>
        ${
          chequeBookName && methodType === "cheque"
            ? `<span class="field"><b>دفتر الشيكات:</b> ${escapeHtml(
                chequeBookName
              )}</span>`
            : ""
        }
      </div>

      <div class="row">
        <span class="field"><b>البنك المسحوب عليه:</b> ${escapeHtml(
          bankName
        )}</span>
      </div>

      <div class="row details-row">
        <span class="field"><b>حق:</b> ${escapeHtml(payment.name)}</span>
        <span class="field"><b>من ثمن الوحدة رقم:</b> ${escapeHtml(
          unitSerial
        )}</span>
        <span class="field"><b>بمشروع:</b> ${escapeHtml(projectName)}</span>
      </div>

      <div class="signatures">
        <span>إدارة الحسابات</span>
        <span>ختم المشروع</span>
        <span>المستلم</span>
      </div>

      <footer>
        <div>
          <div>لا يخل هذا الإيصال بما للبائع من حقوق أخرى</div>
          <div>لا يعتد بالإيصال إلا باستيفاء التوقيعات وختم المشروع</div>
        </div>
        <div><b>تحريراً في /</b> ${escapeHtml(receiptDate)}</div>
      </footer>
    </section>`;
}

export const printReceiptPdf = (
  { base },
  payment: any = {},
  reservation: any = {}
) => {
  const receipt = getLatestReceipt(payment) || {};
  const { receiptTitle } = resolvePaymentMethod(payment, receipt, reservation);

  return `<!DOCTYPE html>
  <html lang="ar" dir="rtl">
    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>${escapeHtml(receiptTitle)}</title>
      <style>
        @font-face {
          font-family: "Cairo";
          src: url("${base}assets/fonts/Cairo-VariableFont_slnt,wght.ttf") format("truetype");
          font-weight: 200 1000;
        }

        * {
          box-sizing: border-box;
        }

        html,
        body {
          margin: 0;
          padding: 0;
        }

        body {
          width: 210mm;
          min-height: 297mm;
          color: #000;
          background: #fff;
          font-family: "Cairo", sans-serif;
          font-size: 9pt;
        }

        .receipt-copy {
          position: relative;
          height: 146mm;
          padding: 8mm 14mm 6mm;
          overflow: hidden;
        }

        .receipt-copy + .receipt-copy {
          border-top: 0.5mm dashed #777;
        }

        .receipt-header {
          position: relative;
          display: grid;
          grid-template-columns: 1fr 2fr 1fr;
          align-items: start;
          min-height: 18mm;
          direction: ltr;
        }

        .serial {
          padding-top: 1mm;
          text-align: left;
          direction: rtl;
        }

        .title {
          text-align: center;
          direction: rtl;
        }

        .title h1 {
          margin: 0 0 1mm;
          font-size: 15pt;
          line-height: 1.4;
        }

        .logo {
          text-align: right;
        }

        .logo img {
          max-width: 42mm;
          max-height: 15mm;
          object-fit: contain;
        }

        .row {
          display: flex;
          align-items: baseline;
          gap: 7mm;
          min-height: 10mm;
          line-height: 1.7;
        }

        .date-row {
          justify-content: flex-start;
        }

        .two-columns .field,
        .amount-row .field {
          flex: 1;
          min-width: 0;
        }

        .words {
          flex: 1.3;
          min-width: 0;
          overflow-wrap: anywhere;
        }

        .payment-row,
        .details-row {
          justify-content: space-between;
        }

        .field {
          min-width: 0;
          overflow-wrap: anywhere;
        }

        .signatures {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          margin-top: 2mm;
          text-align: center;
          font-size: 9.5pt;
        }

        footer {
          position: absolute;
          right: 14mm;
          bottom: 6mm;
          left: 14mm;
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          font-size: 8pt;
          line-height: 1.7;
        }

        @page {
          size: A4 portrait;
          margin: 0;
        }

        @media print {
          html,
          body {
            width: 210mm;
            height: 297mm;
          }

          * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      </style>
    </head>
    <body>
      ${receiptCopy("نسخة الحسابات", payment, reservation)}
      ${receiptCopy("نسخة العميل", payment, reservation)}
    </body>
  </html>`;
};



// WEBPACK FOOTER //
// ./src/app/reservation-view/prints/receipt-pdf.ts